// ============================================================================
// CHARGE TAX MAPPING SERVICE
// ============================================================================
// /api/v1/tax/charge-mappings — list (server-side paged), create, close.
// There is no update or delete: mappings are effective-dated and append-only.

import { get, post } from '../client/axiosClient';
import { chargeTaxMappingEndpoints } from '../endpoints/tax.endpoints';
import type { ApiResponse, BackendPagedResult, PaginatedResponse } from '../types/common.types';
import type {
  ChargeTaxMapping,
  ChargeTaxMappingSearchRequest,
  CloseChargeTaxMappingRequest,
  CreateChargeTaxMappingRequest,
} from '../types/chargeTaxMapping.types';

class ChargeTaxMappingService {
  async getAll(params: ChargeTaxMappingSearchRequest): Promise<PaginatedResponse<ChargeTaxMapping>> {
    const response = await get<ApiResponse<BackendPagedResult<ChargeTaxMapping>>>(
      chargeTaxMappingEndpoints.list(params),
    );
    const raw = response.data;
    const paged = raw.data;

    return {
      success: raw.success,
      message: raw.message,
      data: paged?.items ?? [],
      pagination: {
        currentPage: paged?.page ?? 1,
        pageSize: paged?.pageSize ?? params.pageSize ?? 25,
        totalPages: paged?.totalPages ?? 0,
        totalItems: paged?.totalCount ?? 0,
        hasNextPage: paged?.hasNext ?? false,
        hasPreviousPage: paged?.hasPrevious ?? false,
      },
    };
  }

  async getById(id: string): Promise<ApiResponse<ChargeTaxMapping>> {
    const response = await get<ApiResponse<ChargeTaxMapping>>(chargeTaxMappingEndpoints.get(id));
    return response.data;
  }

  async create(request: CreateChargeTaxMappingRequest): Promise<ApiResponse<ChargeTaxMapping>> {
    const response = await post<ApiResponse<ChargeTaxMapping>>(chargeTaxMappingEndpoints.create(), request);
    return response.data;
  }

  async close(id: string, request: CloseChargeTaxMappingRequest): Promise<ApiResponse<ChargeTaxMapping>> {
    const response = await post<ApiResponse<ChargeTaxMapping>>(chargeTaxMappingEndpoints.close(id), request);
    return response.data;
  }
}

export const chargeTaxMappingService = new ChargeTaxMappingService();
export default chargeTaxMappingService;
